import { useState } from "react";

import { useApi, type ApiFailure } from "../api";
import { useFlow } from "../flow";
import { writeRememberedShopper } from "../session";
import { useTheme } from "../theme";
import { CodeStep, ManualLookup, OrdersStep, PhoneStep } from "./lookup";

/**
 * Step 1 of docs/08: who is this, and which order.
 *
 * The one way in is phone, code, "your orders": the shopper types the mobile she ordered
 * with, gets a one-time code, and picks the order from a list, so nobody has to find an
 * order number. The order number form is a shortcut from there and only from there: the
 * lookup endpoint requires the verified session, so a start on that form would be a form
 * that cannot succeed. A remembered lookup, a storefront hand-off in the URL fragment and
 * a dead deep link therefore all still begin with the phone, and what they remembered
 * becomes a prefill for the order number form once the session exists.
 *
 * What the token is: a bearer minted by `/shopper/verify`, kept in wizard memory only.
 * It stands in for the phone on `/lookup` and `/uploads`, and it is written into the flow
 * as soon as the code is proven, with the phone it proved, because the rest of the wizard
 * (and the order number form) needs both.
 */
type Mode =
  | { kind: "phone" }
  | { kind: "code"; phone: string }
  | { kind: "orders"; phone: string; shopperToken: string }
  | { kind: "manual" };

export function LookupScreen({
  /** The deep-link screen sets this when a minted link failed to resolve. */
  linkFailed = false,
}: {
  linkFailed?: boolean;
} = {}): React.JSX.Element {
  const api = useApi();
  const { applyMerchantTheme } = useTheme();
  const { merchantSlug, phone, shopperToken, update, next } = useFlow();

  const [mode, setMode] = useState<Mode>(() => {
    // No session: the phone, every time. Nothing the browser remembers (a lookup from
    // earlier in the tab, a storefront fragment, a dead link) can start a lookup, so
    // opening on the order number form would be opening on a form that 404s.
    if (shopperToken === null) return { kind: "phone" };
    // Coming BACK from an order (the shopper opened one and returned): the code she
    // typed is still good, so she lands on her own list again. Without this she was
    // dropped onto the order-number form with no way back to the list she came from.
    if (phone !== "") return { kind: "orders", phone, shopperToken };
    // Signed in with no phone on the flow. Defensive: a token is only ever minted for a
    // phone. The list files a picked order under that phone, so it needs one; the form
    // needs only the session, and the session is what we have.
    return { kind: "manual" };
  });
  const [lastPhone, setLastPhone] = useState(phone);

  async function openOrder(
    orderRef: string,
    provenPhone: string,
    shopperToken: string,
  ): Promise<ApiFailure | null> {
    const result = await api.lookup({ merchantSlug, orderRef }, shopperToken);
    if (!result.ok) return result;

    writeRememberedShopper({ merchantSlug, orderRef, phone: provenPhone });
    applyMerchantTheme(result.data.policy.theme);
    update({
      orderRef,
      phone: provenPhone,
      shopperToken,
      lookup: result.data,
      lines: [],
    });
    next();
    return null;
  }

  switch (mode.kind) {
    case "phone":
      return (
        <PhoneStep
          initialPhone={lastPhone}
          linkFailed={linkFailed}
          onSent={(wirePhone) => {
            setLastPhone(wirePhone);
            setMode({ kind: "code", phone: wirePhone });
          }}
        />
      );
    case "code":
      return (
        <CodeStep
          phone={mode.phone}
          onVerified={(shopperToken) => {
            // Into the flow, not just this component's state: the order number form and
            // the evidence upload both read the session off `useFlow()`.
            update({ phone: mode.phone, shopperToken });
            setMode({ kind: "orders", phone: mode.phone, shopperToken });
          }}
          onChangePhone={() => setMode({ kind: "phone" })}
        />
      );
    case "orders":
      return (
        <OrdersStep
          shopperToken={mode.shopperToken}
          onPick={async (orderRef) => {
            const failure = await openOrder(
              orderRef,
              mode.phone,
              mode.shopperToken,
            );
            // A refused pick is rare (the list came from the same session) and the
            // honest recovery is the order number form, which shows the server's words.
            if (failure !== null) setMode({ kind: "manual" });
          }}
          onHaveOrderRef={() => setMode({ kind: "manual" })}
        />
      );
    case "manual":
      return (
        <ManualLookup
          linkFailed={linkFailed}
          onUsePhone={() => setMode({ kind: "phone" })}
        />
      );
  }
}
